import React from 'react';
import { isMobile, isTablet } from '../../utils/deviceUtils';

export const Privacy: React.FC = () => {
  const mobile = isMobile();
  const tablet = isTablet();

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Hero Section */}
      <section className="h-auto bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 p-4 sm:p-6 lg:p-8">
          <div className="text-center pt-28 pb-8">
            <h1
              className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-5xl'} font-black mb-4`}
            >
              Privacy Policy
            </h1>
            <p
              className={`${mobile ? 'text-base' : 'text-lg'} text-white/90 max-w-2xl mx-auto`}
            >
              We are committed to protecting your privacy and ensuring the
              security of your personal information.
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Content */}
      <section
        className={`${mobile ? 'py-16' : tablet ? 'py-16' : 'py-20'} bg-background-primary`}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-soft p-6 sm:p-8 lg:p-12">
            <div className="prose prose-lg max-w-none">
              <p className="text-text-secondary mb-8">
                <strong>Last updated:</strong> January 15, 2024
              </p>

              <section className="mb-8">
                <h2
                  className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}
                >
                  1. Introduction
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Dala Rides ("we," "our," or "us") is committed to protecting
                  your privacy. This Privacy Policy explains how we collect,
                  use, disclose, and safeguard your information when you use our
                  safari booking platform and related services.
                </p>
              </section>

              <section className="mb-8">
                <h2
                  className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}
                >
                  2. Information We Collect
                </h2>

                <h3 className="text-lg font-semibold text-text-primary mb-3">
                  2.1 Personal Information
                </h3>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We collect personal information that you provide directly to
                  us, including:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Name, email address, and phone number</li>
                  <li>Date of birth and passport information</li>
                  <li>Billing and payment information</li>
                  <li>Travel preferences and special requirements</li>
                  <li>Emergency contact information</li>
                  <li>Company information (for operators)</li>
                </ul>

                <h3 className="text-lg font-semibold text-text-primary mb-3">
                  2.2 Automatically Collected Information
                </h3>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We automatically collect certain information when you use our
                  platform:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>
                    Device information (IP address, browser type, operating
                    system)
                  </li>
                  <li>
                    Usage data (pages visited, time spent, search queries)
                  </li>
                  <li>Location data (with your consent)</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2
                  className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}
                >
                  3. How We Use Your Information
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We use the information we collect for the following purposes:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Process and manage your bookings and reservations</li>
                  <li>Communicate with you about your trips and account</li>
                  <li>Provide customer support and respond to inquiries</li>
                  <li>Send marketing communications (with your consent)</li>
                  <li>Improve our services and user experience</li>
                  <li>Ensure platform security and prevent fraud</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2
                  className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}
                >
                  4. Information Sharing and Disclosure
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We may share your information in the following circumstances:
                </p>

                <h3 className="text-lg font-semibold text-text-primary mb-3">
                  4.1 Service Providers
                </h3>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We share information with tour operators, transport providers,
                  and other service providers to fulfill your bookings and
                  provide requested services.
                </p>

                <h3 className="text-lg font-semibold text-text-primary mb-3">
                  4.2 Legal Requirements
                </h3>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We may disclose information when required by law, court order,
                  or government request, or to protect our rights, property, or
                  safety.
                </p>

                <h3 className="text-lg font-semibold text-text-primary mb-3">
                  4.3 Business Transfers
                </h3>
                <p className="text-text-secondary leading-relaxed mb-4">
                  In the event of a merger, acquisition, or sale of assets, your
                  information may be transferred as part of the transaction.
                </p>
              </section>

              <section className="mb-8">
                <h2
                  className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}
                >
                  5. Data Security
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We implement appropriate technical and organizational measures
                  to protect your personal information against unauthorized
                  access, alteration, disclosure, or destruction. These measures
                  include:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Encryption of sensitive data in transit and at rest</li>
                  <li>Regular security assessments and updates</li>
                  <li>Access controls and authentication measures</li>
                  <li>Employee training on data protection</li>
                  <li>Incident response procedures</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2
                  className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}
                >
                  6. Data Retention
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We retain your personal information for as long as necessary
                  to:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Provide our services and maintain your account</li>
                  <li>Comply with legal obligations</li>
                  <li>Resolve disputes and enforce agreements</li>
                  <li>Improve our services</li>
                </ul>
                <p className="text-text-secondary leading-relaxed mb-4">
                  When we no longer need your information, we will securely
                  delete or anonymize it.
                </p>
              </section>

              <section className="mb-8">
                <h2
                  className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}
                >
                  7. Your Rights and Choices
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  You have the following rights regarding your personal
                  information:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>
                    <strong>Access:</strong> Request a copy of your personal
                    information
                  </li>
                  <li>
                    <strong>Correction:</strong> Update or correct inaccurate
                    information
                  </li>
                  <li>
                    <strong>Deletion:</strong> Request deletion of your personal
                    information
                  </li>
                  <li>
                    <strong>Portability:</strong> Receive your data in a
                    portable format
                  </li>
                  <li>
                    <strong>Objection:</strong> Object to certain processing
                    activities
                  </li>
                  <li>
                    <strong>Withdrawal:</strong> Withdraw consent for marketing
                    communications
                  </li>
                </ul>
              </section>

              <section className="mb-8">
                <h2
                  className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}
                >
                  8. Cookies and Tracking Technologies
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We use cookies and similar technologies to:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Remember your preferences and settings</li>
                  <li>Analyze website usage and performance</li>
                  <li>Provide personalized content and recommendations</li>
                  <li>Ensure platform security</li>
                </ul>
                <p className="text-text-secondary leading-relaxed mb-4">
                  You can control cookie settings through your browser
                  preferences, though disabling certain cookies may affect
                  platform functionality.
                </p>
              </section>

              <section className="mb-8">
                <h2
                  className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}
                >
                  9. International Data Transfers
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Your information may be transferred to and processed in
                  countries other than your own. We ensure appropriate
                  safeguards are in place to protect your information in
                  accordance with this Privacy Policy and applicable data
                  protection laws.
                </p>
              </section>

              <section className="mb-8">
                <h2
                  className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}
                >
                  10. Children's Privacy
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Our platform is not intended for children under 13 years of
                  age. We do not knowingly collect personal information from
                  children under 13. If you believe we have collected
                  information from a child under 13, please contact us
                  immediately.
                </p>
              </section>

              <section className="mb-8">
                <h2
                  className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}
                >
                  11. Third-Party Links
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Our platform may contain links to third-party websites or
                  services. We are not responsible for the privacy practices of
                  these third parties. We encourage you to review their privacy
                  policies before providing any personal information.
                </p>
              </section>

              <section className="mb-8">
                <h2
                  className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}
                >
                  12. Changes to This Privacy Policy
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We may update this Privacy Policy from time to time. We will
                  notify you of any material changes by posting the new Privacy
                  Policy on our platform and updating the "Last updated" date.
                  Your continued use of our platform after such changes
                  constitutes acceptance of the updated Privacy Policy.
                </p>
              </section>

              <section className="mb-8">
                <h2
                  className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}
                >
                  13. Contact Us
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy or our
                  data practices, please contact us:
                </p>
                <div className="bg-background-secondary rounded-xl p-4 mt-4">
                  <p className="text-text-secondary">
                    <strong>Email:</strong> privacy@dalarides.com
                    <br />
                    <strong>Phone:</strong> +255 123 456 789
                    <br />
                    <strong>Address:</strong> 123 Safari Street, Arusha,
                    Tanzania
                    <br />
                    <strong>Data Protection Officer:</strong> dpo@dalarides.com
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
